import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
} from 'react';
import { text, unitList } from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingInfo,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import clearanceCalculationsReducer, {
	initialClearanceCalculationsState,
} from './reducer';
import ClearanceForm from './clearance-form/ClearanceForm';
import {
	getPartStateFromParams,
	updateToolParams,
	getPartNumberFromUrl,
} from '../utils/engineering-tools';
import { FittingCalculationResults } from '../types/types';
import { trackStart } from '../utils/analytics';
import PartPreSearch from '../components/PartPreSearch';
import {taperedBoreMessage} from "../../components/bearing-search/api/api";
import DOMPurify from "dompurify";

const ClearanceCalculations: React.FC = () => {
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ savedFittings, setSavedFittings ] = useState<
		Partial<FittingCalculationResults> | undefined
	>();
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ formState, dispatch ] = useReducer(
		clearanceCalculationsReducer,
		initialClearanceCalculationsState,
	);

	// Parse tool parameters if it's from recommended link
	useEffect( () => {
		trackStart( 'bearingClearanceCalculations' );

		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
				handleSubmit();
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setSearchInput( savedPart.marketPlaceDescription || '' );
				setIsPartSelected( true );
			}
		}
	}, [] );

	// Pass down part number to next recommended tool
	useEffect( () => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [ partState.marketPlaceDescription ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}

		setIsPartOpen( false );
	};

	const handleSubmit = ( event?: FormEvent ) => {
		if (event) {
			event.preventDefault();
		}

		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		}
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsPartSelected( false );
		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
		} );
	};

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};

	const setPartFromPreSearchAndSubmit = (value: string, e: React.MouseEvent<HTMLElement>) => {
		setSearchInput( value );
		handleSubmit(e);
	}

	return (
		<>
			<div>
				<form onSubmit={handleSubmit}>
					<div className="t-engineering-tools__form-fields t-engineering-tools__form-fields--multi-column timken-tool-presearch-container">
						<TextInput
							id="clearance_calculation_part_number"
							required={true}
							label={text.partNumber}
							verified={!! partState.partNumber}
							name="partNumber"
							value={searchInput}
							disabled={!! partState.partNumber}
							placeholder={text.partNumber}
							message={
								partState.partNumber
									? `${ text.bearingType }: ${ partState.bearingTypeLabel || partState.typeDescription }`
									: text.partWildCardHint
							}
							onChange={inputPartNumber}
						/>

						<PartPreSearch
							input={searchInput}
							isHidden={searchInput.length < 2 || isPartOpen || isPartSelected}
							unit={formState.unit}
							onClick={preSearchClickHandler}
							verifyPartNumber={(value: string, e) => setPartFromPreSearchAndSubmit(value, e)}
						/>

						<SelectDropdown
							id="clearance_calculation_unit"
							name="unit"
							required={true}
							label={text.displayUnits}
							value={formState.unit}
							options={unitList}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_unit',
									payload: event.target.value as TimkenUnit,
								} )
							}
						/>

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! searchInput}
							>
								{text.lookup}
							</button>
						)}

						{!! partState.partNumber && (
							<>
								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearSearch()}
								>
									{text.clear}
								</button>
							</>
						)}
					</div>
					{partState.taperedBore === 'Y' && (
						<div className="t-engineering-tools__tapered-bore-message"
							 dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(taperedBoreMessage)}}
						/>
					)}
				</form>
			</div>

			{!! partState.bearingType && partState.taperedBore === 'N' && (
				<ClearanceForm
					unit={formState.unit}
					bearing={partState}
					fittings={savedFittings}
					onClear={clearSearch}
				/>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					units={formState.unit}
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};

export default ClearanceCalculations;
